import { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Route, Stamp, Radio, Camera, WifiOff, ArrowRight, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import WorldMapHero from "@/components/WorldMapHero";
import InviteCodeForm from "@/components/InviteCodeForm";

const CHAPTERS = [
  { icon: MapPin, title: "Pin it", body: "Tap the map, search a place, or snap a photo — Pins reads the GPS inside your pictures and drops the pin for you." },
  { icon: Route, title: "Chart it", body: "String pins into trips and watch a dashed route unfold across the atlas, stop by stop." },
  { icon: Stamp, title: "Stamp it", body: "Every new country and state presses a stamp into your passport book. Distance, milestones, flags — tallied for you." },
  { icon: Radio, title: "Share it", body: "Send a trip home with a magic link, invite companions to add their own pins, or tell a trip as a full-screen story." },
  { icon: Camera, title: "Keep it", body: "Notes, dates and photographs turn each pin into a journal entry you'll actually reread." },
  { icon: WifiOff, title: "Take it anywhere", body: "Works offline on the trail — pins and photos sync themselves when you're back in range." },
];

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | already
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setState("busy");
    try {
      const { data } = await api.post("/launch/subscribe", { email, name: name || undefined });
      setState(data.already ? "already" : "done");
    } catch (err) {
      setState("idle");
      setError(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f5f0] text-[#1f1f1f]" data-testid="coming-soon-page">
      {/* Hero band — same atlas treatment as the sign-in page */}
      <section className="relative bg-[#1E3329] text-[#f7f5f0] overflow-hidden border-b-2 border-[#1f1f1f]">
        <div className="absolute inset-0"><WorldMapHero fit="cover" overlayOpacity={0.55} /></div>
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-16 lg:pt-10 lg:pb-24">
          <div className="flex items-center gap-3">
            <img src="/logo.svg" alt="Pins" className="w-9 h-9 bg-[#f7f5f0]" />
            <div>
              <p className="font-serif text-xl leading-none">Pins</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#f7f5f0]/70">A travel journal</p>
            </div>
          </div>
          <div className="mt-14 lg:mt-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-end">
            <div>
              <p className="overline text-[#f3c3b5]">Opening soon · pinmy.travel</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3 max-w-xl">
                The maps are almost <em className="text-[#e8a08f]">ready.</em>
              </h1>
              <p className="mt-6 text-base md:text-lg text-[#f7f5f0]/85 max-w-md leading-relaxed">
                Pins is a living atlas of everywhere you've walked, eaten, listened and lingered — pins, trips,
                passport stamps and the stories in between. We're putting the last ink on it now.
              </p>
            </div>

            <div className="tactile-card bg-[#f7f5f0] text-[#1f1f1f] p-6 sm:p-8 shadow-[8px_8px_0_#1f1f1f]" data-testid="launch-signup-card">
              {state === "done" || state === "already" ? (
                <div data-testid="launch-signup-success">
                  <p className="overline text-[#b85c47]">{state === "already" ? "Already aboard" : "You're on the manifest"}</p>
                  <p className="font-serif text-3xl mt-2 leading-tight">
                    {state === "already" ? "We have your seat reserved." : "Your seat is reserved."}
                  </p>
                  <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
                    We'll write to <span className="font-medium text-[#1f1f1f]">{email}</span> the moment the doors open.
                    Until then — keep collecting places.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 border-2 border-[#2c4a3e] text-[#2c4a3e] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] -rotate-2">
                    <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> Admitted · {new Date().getFullYear()}
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="launch-signup-form">
                  <p className="overline text-[#b85c47]">Be first through the gate</p>
                  <p className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">Tell us where to send the invitation.</p>
                  <div className="mt-5 space-y-4">
                    <div>
                      <label className="field-label" htmlFor="launch-name">First name <span className="normal-case tracking-normal text-[#8a8a8a]">(optional)</span></label>
                      <input id="launch-name" className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Amelia" data-testid="launch-name-input" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="launch-email">Email</label>
                      <input id="launch-email" type="email" required className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@somewhere.com" data-testid="launch-email-input" />
                    </div>
                    {error && <p className="text-sm text-[#963f2b] font-mono" data-testid="launch-signup-error">{error}</p>}
                    <button type="submit" disabled={state === "busy"} className="btn-terracotta w-full !py-3 flex items-center justify-center gap-2" data-testid="launch-signup-submit">
                      {state === "busy" ? "Stamping…" : <>Notify me at launch <ArrowRight className="w-4 h-4" strokeWidth={2} /></>}
                    </button>
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8a8a8a] text-center">
                      One email when we open · no newsletters · unsubscribe any time
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What Pins is */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-20" data-testid="coming-soon-chapters">
        <p className="overline text-[#b85c47]">What's inside</p>
        <h2 className="font-serif text-base md:text-lg mt-1 text-[#5c5c5c]">Six ways to keep the road with you</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1f1f1f] border-2 border-[#1f1f1f]">
          {CHAPTERS.map((c, i) => (
            <div key={c.title} className="bg-[#f7f5f0] p-6 lg:p-8" data-testid={`coming-soon-chapter-${i}`}>
              <p className="overline text-[#b85c47] flex items-center gap-2">
                <c.icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif text-2xl mt-2">{c.title}</h3>
              <p className="mt-2 text-sm text-[#5c5c5c] leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Promise band */}
      <section className="bg-[#2c4a3e] text-[#f7f5f0] border-y-2 border-[#1f1f1f]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="font-serif text-2xl sm:text-3xl italic">Track every place you've ever been.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#f7f5f0]/70">
            Your pins · your photos · your map — private by default
          </p>
        </div>
      </section>

      <footer className="bg-[#1f1f1f] text-[#f7f5f0]" data-testid="coming-soon-footer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-4">
          <InviteCodeForm />
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pb-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#f7f5f0]/70">Pins · pinmy.travel · a travel journal</p>
          <nav className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.18em]">
            <Link to="/terms" className="underline underline-offset-4 hover:text-[#b85c47]">Terms</Link>
            <Link to="/privacy" className="underline underline-offset-4 hover:text-[#b85c47]">Privacy</Link>
            <a href="mailto:hello@pinmy.travel" className="underline underline-offset-4 hover:text-[#b85c47]">Contact</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
